import React, { useState, useEffect, useRef } from 'react';
import './CommentPanel.css';

const CommentPanel = ({ pdfId, comments, onCommentsChange, onDeleteComment, onUpdateComment, onReplyComment, onMarkCommentRead, userName, selectedCommentId, onSelectComment }) => {

  // Note: CommentPanel no longer creates its own socket connection
  // All socket events are handled by PDFViewer to avoid duplicate connections
  // Delete and update operations are passed as callbacks from PDFViewer

  const selectedItemRef = useRef(null);
  const listRef = useRef(null);

  // Scroll the selected comment into view in the panel when selection changes
  useEffect(() => {
    if (selectedCommentId && selectedItemRef.current && listRef.current) {
      selectedItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [selectedCommentId]);

  const handleDelete = (commentId) => {
    if (onDeleteComment) {
      onDeleteComment(commentId);
    }
  };

  const handleUpdate = (commentId, newText) => {
    if (onUpdateComment) {
      onUpdateComment(commentId, newText);
    }
  };

  return (
    <div className="comment-panel">
      {/* s */}

      <div className="comments-list" ref={listRef}>
        {comments.length === 0 ? (
          <div className="no-comments">
            <p>No comments yet.</p>
            <p className="hint">Click "Add Comment" and click on the PDF to add one!</p>
          </div>
        ) : (
          comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
              onReply={onReplyComment}
              onMarkRead={onMarkCommentRead}
              userName={userName}
              isSelected={selectedCommentId === comment.id}
              onSelect={() => onSelectComment?.(comment)}
              itemRef={selectedCommentId === comment.id ? selectedItemRef : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
};

const CommentItem = ({ comment, onDelete, onUpdate, onReply, onMarkRead, userName, isSelected, onSelect, itemRef }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [replyText, setReplyText] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);

  const handleSave = () => {
    if (editText.trim() && editText !== comment.text) {
      onUpdate(comment.id, editText.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditText(comment.text);
    setIsEditing(false);
  };

  const handleReplySubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = replyText.trim();
    if (!text || !onReply) return;
    const reply = {
      id: `reply-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      author: userName || 'User',
      text,
      timestamp: new Date().toISOString()
    };
    onReply(comment.id, reply);
    setReplyText('');
    setShowReplyInput(false);
  };

  const replies = Array.isArray(comment.replies) ? comment.replies : [];

  const isResolved = !!comment.isRead;

  return (
    <div
      className={`comment-item ${isResolved ? 'comment-item-resolved' : ''} ${isSelected ? 'comment-item-selected' : ''}`}
      ref={itemRef}
      onClick={() => !isEditing && !showReplyInput && onSelect?.()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isEditing && !showReplyInput) { e.preventDefault(); onSelect?.(); } }}
      aria-pressed={isSelected}
    >
      <div className="comment-header">
        <div className="comment-author">
          <span className="author-name">
            {comment.author}
            {comment.isFromPDF && (
              <span className="pdf-annotation-badge" title="Extracted from PDF">
                📄 PDF
              </span>
            )}
            {comment.isRead && (
              <span className="comment-read-badge" title="Marked as read"> </span>
            )}
          </span>
          <span className="comment-meta">
            Page {comment.page} • {new Date(comment.timestamp).toLocaleString()}
          </span>
        </div>
        <div className="comment-actions" onClick={(e) => e.stopPropagation()}>
          {/* <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowReplyInput(true); }}
            className="comment-action-btn reply-btn"
            title="Reply"
          >
            Reply
          </button> */}
          {/* <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMarkRead?.(comment.id); }}
            className={`comment-action-btn mark-read-btn ${isResolved ? 'is-resolved' : ''}`}
            title={isResolved ? 'Marked as resolved' : 'Mark as read (resolved)'}
          >
            {isResolved ? 'Resolved' : 'Mark as read'}
          </button> */}
          <button
            onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
            className="edit-btn"
            title="Edit comment"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(comment.id); }}
            className="delete-btn"
            title="Delete comment"
          >
            🗑️
          </button>
        </div>
      </div>
      
      {isEditing ? (
        <div className="comment-edit" onClick={(e) => e.stopPropagation()}>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="edit-textarea"
            rows="3"
          />
          <div className="edit-actions">
            <button onClick={handleSave} className="save-btn">Save</button>
            <button onClick={handleCancel} className="cancel-btn">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="comment-text">
          {comment.text || <em className="empty-comment">No text</em>}
        </div>
      )}

      {replies.length > 0 && (
        <div className="comment-replies" onClick={(e) => e.stopPropagation()}>
          {replies.map(r => (
            <div key={r.id} className="comment-reply-item">
              <strong>{r.author}:</strong> {r.text}
              <span className="comment-reply-meta">{new Date(r.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {showReplyInput && (
        <form className="comment-reply-form" onSubmit={handleReplySubmit} onClick={(e) => e.stopPropagation()}>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
            className="comment-reply-input"
            rows="2"
            autoFocus
          />
          <div className="comment-reply-actions">
            <button type="submit" className="reply-submit-btn" disabled={!replyText.trim()}>Reply</button>
            <button type="button" className="reply-cancel-btn" onClick={() => { setShowReplyInput(false); setReplyText(''); }}>Cancel</button>
          </div>
        </form>
      )}

      <div className="comment-card-footer" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowReplyInput(true); }}
          className="comment-action-btn reply-btn"
          title="Reply"
        >
          Reply
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMarkRead?.(comment.id, !isResolved); }}
          className={`comment-action-btn mark-read-btn ${isResolved ? 'is-resolved' : ''}`}
          title={isResolved ? 'Mark as unresolved' : 'Mark as resolved'}
        >
          {isResolved ? 'Mark as unresolved' : 'Mark as resolved'}
        </button>
      </div>
    </div>
  );
};

export default CommentPanel;

